import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { s as setInputRef, g as getError, a as getValidityObject, c as checkValidity, r as reportValidity, b as setCustomValidity, w as watchError, d as watchWarning, e as getHostAttributes, f as getFormFieldAttributes } from './index7.js';
import { a as isKeyOfObject } from './index12.js';
import { W as WuiDateRangeInput, d as defineCustomElement$k } from './date-range-input.js';
import { r as rangeWithinMaxDays } from './util3.js';
import { g as getDateRangePresets } from './constants.js';
import { d as defineCustomElement$r } from './badge.js';
import { d as defineCustomElement$q } from './box.js';
import { d as defineCustomElement$p } from './button.js';
import { d as defineCustomElement$o } from './check-input.js';
import { d as defineCustomElement$n } from './close-button.js';
import { d as defineCustomElement$m } from './date-input.js';
import { f as format, d as defineCustomElement$l } from './date-input-calendar.js';
import { d as defineCustomElement$j } from './divider.js';
import { d as defineCustomElement$i } from './form-field.js';
import { d as defineCustomElement$h } from './icon.js';
import { d as defineCustomElement$g } from './input.js';
import { d as defineCustomElement$f } from './input-button.js';
import { d as defineCustomElement$e } from './input-menu.js';
import { d as defineCustomElement$d } from './layout.js';
import { d as defineCustomElement$c } from './list.js';
import { d as defineCustomElement$b } from './list-item.js';
import { d as defineCustomElement$a } from './list-item-divider.js';
import { d as defineCustomElement$9 } from './list-item-header.js';
import { d as defineCustomElement$8 } from './list-item-meta.js';
import { d as defineCustomElement$7 } from './loader.js';
import { d as defineCustomElement$6 } from './menu-pane.js';
import { d as defineCustomElement$5 } from './select-input.js';
import { d as defineCustomElement$4 } from './status.js';
import { d as defineCustomElement$3 } from './tooltip.js';
import { d as defineCustomElement$2 } from './tooltip-label.js';
import { i as isBefore, b as isAfter } from './index13.js';

const dateRangeInputWithPresetsCss = ":host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;box-sizing:border-box;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}wui-select-input{width:var(--jw-container-xs)}";
const WuiDateRangeInputWithPresetsStyle0 = dateRangeInputWithPresetsCss;

const isRangeInvalid = ([start, end], { minDate, maxDate, maxDays } = {}) => {
    if (minDate && ((start && isBefore(start, minDate)) || (end && isBefore(end, minDate)))) {
        return true;
    }
    if (maxDate && ((start && isAfter(start, maxDate)) || (end && isAfter(end, maxDate)))) {
        return true;
    }
    if (!rangeWithinMaxDays({ start, end }, maxDays)) {
        return true;
    }
    return false;
};
const WuiDateRangeInputWithPresets$1 = /*@__PURE__*/ proxyCustomElement(class WuiDateRangeInputWithPresets extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.validityChecked = createEvent(this, "validityChecked", 7);
        this.validityReported = createEvent(this, "validityReported", 7);
        this.setInputRef = setInputRef(this);
        this.dirty = false;
        this.getError = getError(this);
        this.validateInternal = () => {
            const dateRangeInputElm = this.elm.shadowRoot?.querySelector('wui-date-range-input');
            if (!dateRangeInputElm) {
                return;
            }
            // Let inner input handle validation
            dateRangeInputElm.checkValidity();
            return {
                validationMessage: '', // Keep empty as the inner inputs will report their validity
                validity: dateRangeInputElm.validity,
            };
        };
        this.handlePresetChange = async (e) => {
            // We fire our own input event
            e.stopPropagation();
            e.stopImmediatePropagation();
            const newPreset = e.target?.value;
            if (!isKeyOfObject(newPreset, this.filteredPresets)) {
                return;
            }
            this.selectedPresetName = newPreset;
            const foundPreset = this.filteredPresets[newPreset];
            if (!foundPreset) {
                throw new Error('Undefined: foundPreset');
            }
            // Toggle date range input to readonly when we're using an invalid preset
            this.readOnlyDateRangeInput = !isRangeInvalid(foundPreset.value(), this.dateRangeInput);
            // Allow Stencil to re-render the date-range-input before updating value and re-triggering validation
            await new Promise((r) => setTimeout(r, 1));
            let newValue;
            // Keep existing value when switching to custom and is within our limits
            if (this.selectedPresetName === 'CUSTOM' &&
                // Check if existing value is within limits
                this.valueAsDateRange &&
                !isRangeInvalid(this.valueAsDateRange, this.dateRangeInput)) {
                // We do want to call `updateValue` to re-trigger our input event.
                // The date range may be the same, but the preset changed.
                newValue = this.value;
            }
            else {
                newValue = WuiDateRangeInput.stringifyValue(foundPreset.value());
            }
            this.updateValue(newValue);
        };
        this.handleDateRangeChange = (e) => {
            // We fire our own input event
            e.stopPropagation();
            e.stopImmediatePropagation();
            if (this.selectedPresetName !== 'CUSTOM') {
                this.selectedPresetName = 'CUSTOM';
            }
            const value = e.target?.value;
            if (!value) {
                return;
            }
            const matchingPresetName = value ? this.findPresetNameForRange(value) : undefined;
            if (matchingPresetName) {
                this.selectedPresetName = matchingPresetName;
            }
            this.updateValue(value);
        };
        this.value = undefined;
        this.required = false;
        this.error = undefined;
        this.warning = undefined;
        this.validate = undefined;
        this.validity = getValidityObject();
        this.validationMessage = '';
        this.customValidation = undefined;
        this.checkValidity = checkValidity(this);
        this.reportValidity = reportValidity(this, () => {
            if (!this.elm.shadowRoot) {
                return;
            }
            return this.elm.shadowRoot.querySelectorAll('wui-date-range-input');
        });
        this.setCustomValidity = setCustomValidity(this);
        this.showValidity = false;
        this.formField = undefined;
        this.name = undefined;
        this.disabled = undefined;
        this.showDescription = false;
        this.dateRangeInput = undefined;
        this.selectInput = undefined;
        this.presets = getDateRangePresets();
        this.autoFilterPresets = true;
        this.readOnlyDateRangeInput = false;
        this.selectedPresetName = 'CUSTOM';
        this.valueAsDateRange = undefined;
        this.valueAsPresetName = undefined;
    }
    _watchError(...args) {
        watchError(this, ...args);
    }
    _watchWarning(...args) {
        watchWarning(this, ...args);
    }
    get selectedPreset() {
        return this.selectedPresetName && this.selectedPresetName in this.filteredPresets
            ? this.filteredPresets[this.selectedPresetName]
            : undefined;
    }
    /**
     * Filter presets based on restrictions. This property should always be prefered to directly reading `this.presets`.
     */
    get filteredPresets() {
        const ret = { ...this.presets };
        let shouldDisableDateRangeInput = false;
        // Check if preset is within constraints
        for (const [presetName, preset] of Object.entries(ret)) {
            if (isRangeInvalid(preset.value(), this.dateRangeInput)) {
                if (this.autoFilterPresets) {
                    delete ret[presetName];
                }
                else {
                    shouldDisableDateRangeInput = true;
                }
            }
        }
        this.readOnlyDateRangeInput = shouldDisableDateRangeInput;
        return ret;
    }
    findPresetNameForRange(rangeString) {
        return Object.keys(this.filteredPresets).find((presetName) => {
            const preset = this.filteredPresets[presetName];
            if (!preset) {
                return;
            }
            const presetRangeString = WuiDateRangeInput.stringifyValue(preset.value());
            return rangeString === presetRangeString;
        });
    }
    updateValue(newValue) {
        this.value = newValue;
        this.elm.dispatchEvent(new window.Event('input', { bubbles: true }));
        this.dirty = true;
        this.checkValidity();
    }
    getDateRangeDescription() {
        if (!this.valueAsDateRange) {
            return '';
        }
        const [start, end] = this.valueAsDateRange;
        if (!(start && end)) {
            return '';
        }
        const startDate = new Date(start);
        const endDate = new Date(end);
        const isSameYear = startDate.getFullYear() === endDate.getFullYear();
        // Format start date differently based on whether years match
        const formattedStart = isSameYear ? format(startDate, 'MMM dd') : format(startDate, "MMM dd ''yy");
        const formattedEnd = format(endDate, "MMM dd ''yy");
        return `Chosen dates: ${formattedStart} - ${formattedEnd}`;
    }
    componentWillLoad() {
        if (!this.value) {
            return;
        }
        const foundPreset = this.findPresetNameForRange(this.value);
        if (foundPreset) {
            this.selectedPresetName = foundPreset;
        }
    }
    render() {
        return (h(Host, { key: '1b0a32c9705a58142618fa01f78a82d26c9be05b', ...getHostAttributes(this) }, h("wui-form-field", { key: 'bf815a47a425cbf383c6c54e5964d7c470f1d883', ...getFormFieldAttributes(this), description: this.showDescription ? this.getDateRangeDescription() : undefined }, h("wui-layout", { key: 'd28b6683df093f9454987dbb53a8d0f408403614', type: "field-field-group" }, h("wui-select-input", { key: 'e2671afa954ae82261dfd7e9a7ea1a6a80838a53', options: Object.entries(this.filteredPresets).map(([presetName, preset]) => {
                return {
                    label: preset.label,
                    value: presetName,
                };
            }), value: this.selectedPresetName, onInput: this.handlePresetChange, disabled: this.disabled, formField: {
                label: 'Timeframe',
            }, ...this.selectInput }), h("wui-date-range-input", { key: 'e394399d8ae63d794740681f65ce4e3a4b5b7e9e', ...this.dateRangeInput, required: this.required, disabled: this.disabled, readOnly: this.readOnlyDateRangeInput && this.selectedPresetName !== 'CUSTOM', value: this.value, onInput: this.handleDateRangeChange })))));
    }
    get elm() { return this; }
    static get watchers() { return {
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }; }
    static get style() { return WuiDateRangeInputWithPresetsStyle0; }
}, [1, "wui-date-range-input-with-presets", {
        "value": [1025],
        "required": [8],
        "error": [8],
        "warning": [8],
        "validate": [16],
        "validity": [1040],
        "validationMessage": [1025, "validation-message"],
        "checkValidity": [16],
        "reportValidity": [16],
        "setCustomValidity": [16],
        "formField": [16],
        "name": [1],
        "disabled": [4],
        "showDescription": [4, "show-description"],
        "dateRangeInput": [16],
        "selectInput": [16],
        "presets": [16],
        "autoFilterPresets": [4, "auto-filter-presets"],
        "valueAsDateRange": [1040],
        "valueAsPresetName": [1025, "value-as-preset-name"],
        "customValidation": [32],
        "showValidity": [32],
        "readOnlyDateRangeInput": [32],
        "selectedPresetName": [32]
    }, undefined, {
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }]);
WuiDateRangeInputWithPresets$1.PRESETS = getDateRangePresets;
Object.defineProperties(WuiDateRangeInputWithPresets$1.prototype, {
    valueAsDateRange: {
        configurable: true,
        get() {
            return WuiDateRangeInput.parseValue(this.value);
        },
        set(newValue) {
            this.value = newValue ? WuiDateRangeInput.stringifyValue(newValue) : undefined;
            this.selectedPresetName = (this.value ? this.findPresetNameForRange(this.value) : undefined) ?? 'CUSTOM';
            this.dirty = true;
            this.checkValidity();
        },
    },
    valueAsPresetName: {
        configurable: true,
        get() {
            return this.selectedPresetName;
        },
        set(newValue) {
            const preset = newValue && newValue in this.filteredPresets ? this.filteredPresets[newValue] : undefined;
            if (!preset) {
                return;
            }
            this.value = WuiDateRangeInput.stringifyValue(preset.value());
            this.selectedPresetName = newValue;
            this.dirty = true;
            this.checkValidity();
        },
    },
});
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-date-range-input-with-presets", "wui-badge", "wui-box", "wui-button", "wui-check-input", "wui-close-button", "wui-date-input", "wui-date-input-calendar", "wui-date-range-input", "wui-divider", "wui-form-field", "wui-icon", "wui-input", "wui-input-button", "wui-input-menu", "wui-layout", "wui-list", "wui-list-item", "wui-list-item-divider", "wui-list-item-header", "wui-list-item-meta", "wui-loader", "wui-menu-pane", "wui-select-input", "wui-status", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-date-range-input-with-presets":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiDateRangeInputWithPresets$1);
            }
            break;
        case "wui-badge":
            if (!customElements.get(tagName)) {
                defineCustomElement$r();
            }
            break;
        case "wui-box":
            if (!customElements.get(tagName)) {
                defineCustomElement$q();
            }
            break;
        case "wui-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$p();
            }
            break;
        case "wui-check-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$o();
            }
            break;
        case "wui-close-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$n();
            }
            break;
        case "wui-date-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$m();
            }
            break;
        case "wui-date-input-calendar":
            if (!customElements.get(tagName)) {
                defineCustomElement$l();
            }
            break;
        case "wui-date-range-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$k();
            }
            break;
        case "wui-divider":
            if (!customElements.get(tagName)) {
                defineCustomElement$j();
            }
            break;
        case "wui-form-field":
            if (!customElements.get(tagName)) {
                defineCustomElement$i();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$h();
            }
            break;
        case "wui-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$g();
            }
            break;
        case "wui-input-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$f();
            }
            break;
        case "wui-input-menu":
            if (!customElements.get(tagName)) {
                defineCustomElement$e();
            }
            break;
        case "wui-layout":
            if (!customElements.get(tagName)) {
                defineCustomElement$d();
            }
            break;
        case "wui-list":
            if (!customElements.get(tagName)) {
                defineCustomElement$c();
            }
            break;
        case "wui-list-item":
            if (!customElements.get(tagName)) {
                defineCustomElement$b();
            }
            break;
        case "wui-list-item-divider":
            if (!customElements.get(tagName)) {
                defineCustomElement$a();
            }
            break;
        case "wui-list-item-header":
            if (!customElements.get(tagName)) {
                defineCustomElement$9();
            }
            break;
        case "wui-list-item-meta":
            if (!customElements.get(tagName)) {
                defineCustomElement$8();
            }
            break;
        case "wui-loader":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "wui-menu-pane":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "wui-select-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-status":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}
defineCustomElement$1();

const WuiDateRangeInputWithPresets = WuiDateRangeInputWithPresets$1;
const defineCustomElement = defineCustomElement$1;

export { WuiDateRangeInputWithPresets, defineCustomElement };

//# sourceMappingURL=wui-date-range-input-with-presets.js.map